import { useEffect, useState, type RefObject } from 'react';

export interface UseIntersectionFadeInOptions {
  threshold?: number;
  rootMargin?: string;
  once?: boolean;
}

export default function useIntersectionFadeIn<T extends Element>(
  ref: RefObject<T | null>,
  options: UseIntersectionFadeInOptions = {},
): boolean {
  const { threshold = 0.15, rootMargin = '0px 0px -80px 0px', once = true } = options;
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    if (typeof IntersectionObserver === 'undefined') {
      setVisible(true);
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setVisible(true);
            if (once) observer.disconnect();
          } else if (!once) {
            setVisible(false);
          }
        }
      },
      { threshold, rootMargin },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [ref, threshold, rootMargin, once]);

  return visible;
}
